import { type PaginationState } from '@tanstack/react-table'
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react'

import { MEDICAL_PLAN_PAGINATION } from './constants'
import { FILTERS_ALLOWING_EMPTY_VALUES, OPTIONAL_BOOLEAN_FILTER_KEYS } from './filterConversions'
import { useMedicalPlanFilters } from './hooks/useMedicalPlanFilters'
import { type MedicalPlanFilters } from './hooks/useMedicalPlanFilters'
import { useMedicalPlans } from './hooks/useMedicalPlans'
import MedicalPlanFiltersView from './MedicalPlanFiltersView'
import MedicalPlansQuickFilters from './MedicalPlansQuickFilters'
import { MedicalPlansTable } from './MedicalPlansTable'

export function MedicalPlansExplorerPage () {
  const { filters, updateFilters, clearFilters } = useMedicalPlanFilters()
  const { data, isLoading, error } = useMedicalPlans(filters)

  // Type guard for error
  const typedError = error instanceof Error ? error : error ? new Error(String(error)) : null

  // Key to force remount of filters view when clearing
  const [filtersKey, setFiltersKey] = useState(0)

  // Pagination state for EnterpriseFiltersView
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: (filters.page || MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_NUMBER) - 1, // TanStack uses 0-based indexing
    pageSize: filters.perPage || MEDICAL_PLAN_PAGINATION.DEFAULT_PAGE_SIZE,
  })

  // Create a setFilters function that matches the expected signature
  // This handles both function updates and direct object updates, and syncs to URL
  const setFilters: Dispatch<SetStateAction<MedicalPlanFilters>> = useMemo(
    () => (value: SetStateAction<MedicalPlanFilters>) => {
      const newFilters = typeof value === 'function' ? value(filters) : value

      // Convert the new filters to partial updates, handling empty values
      const updates = Object.entries(newFilters).reduce<Partial<MedicalPlanFilters>>((acc, [key, val]) => {
        // Skip pagination fields
        if (key === 'page' || key === 'perPage') {
          return acc
        }

        // For filters that allow empty values or are optional booleans, keep empty strings
        // For other filters, convert empty strings and empty arrays to undefined
        const shouldKeepEmpty = OPTIONAL_BOOLEAN_FILTER_KEYS.has(key) || FILTERS_ALLOWING_EMPTY_VALUES.has(key)
        const isEmpty = val === '' || (Array.isArray(val) && val.length === 0)

        return {
          ...acc,
          [key]: (isEmpty && !shouldKeepEmpty) ? undefined : val,
        }
      }, {})

      updateFilters(updates)
    },
    [filters, updateFilters]
  )

  return (
    <div className='max-w-[2400px] mx-auto px-4 pb-6'>
      <div className='flex-col w-full h-full p-4 space-y-4 bg-white rounded-lg max-w-[2400px] mx-auto'>
        <div className='flex items-center justify-between w-full mb-2 gap-x-2'>
          <div className='text-2xl font-semibold w-96'>Medical Plan Explorer</div>
          <div className='flex items-center justify-end w-full gap-x-2'>
            <button
              type='button'
              onClick={() => {
                clearFilters()
                setFiltersKey((prev) => prev + 1)
              }}
              className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thatch-500'
            >
              Clear Filters
            </button>
          </div>
        </div>
        <div className='text-sm text-gray-600 mb-2'>
          Only plans available through the Thatch Brokerage are shown in this tool.
        </div>

        {/* Quick filters at the top */}
        <MedicalPlansQuickFilters filters={filters} setFilters={setFilters} />

        {/* Advanced filters */}
        <MedicalPlanFiltersView
          key={filtersKey}
          filters={filters}
          setFilters={setFilters}
          pagination={pagination}
          setPagination={setPagination}
        />

        {/* Table below filters */}
        <div>
          <MedicalPlansTable
            plans={data?.data}
            pagination={data?.pagination}
            isLoading={isLoading}
            error={typedError}
            onPageChange={(page) => updateFilters({ page })}
            onPerPageChange={(perPage) => updateFilters({ perPage })}
            onFilterByCarrier={(carrierName) => updateFilters({ carrierNames: [carrierName] })}
          />
        </div>
      </div>
    </div>
  )
}
